import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Typography,
  Alert,
  Fade,
  LinearProgress,
  alpha,
  Card,
} from "@mui/material";
import { 
  Visibility, 
  VisibilityOff, 
  Lock,
  LockOpen,
  CheckCircle,
  VpnKey,
} from "@mui/icons-material";
import { ChangePasswordPayload, changePasswordRequest } from "../api/changePass";

// Modern theme colors - matching CreateUserDialog
const THEME = {
  // Backgrounds (lighter for better visibility)
  BG_PRIMARY: '#1a1a1a',
  BG_SECONDARY: '#242424',
  BG_CARD: '#2d2d2d',
  
  // Text
  TEXT_PRIMARY: '#ffffff',
  TEXT_SECONDARY: '#e0e0e0',
  TEXT_MUTED: '#9e9e9e',
  TEXT_LABEL: '#b0b0b0',
  
  // Accents
  ACCENT_PRIMARY: '#2196f3',
  ACCENT_SUCCESS: '#4caf50',
  ACCENT_WARNING: '#ff9800',
  ACCENT_ERROR: '#f44336',
  
  // Borders
  BORDER: '#404040',
  BORDER_LIGHT: '#525252',
};

interface ChangePasswordProps {
onSuccess?: () => void;
}

// Common TextField styles - lighter and more visible
const textFieldSx = {
  '& .MuiInputLabel-root': {
    color: THEME.TEXT_LABEL,
    fontSize: '0.95rem',
    fontWeight: 500,
    backgroundColor: THEME.BG_CARD,
    px: 0.5,
    '&.Mui-focused': {
      color: THEME.ACCENT_PRIMARY,
      fontWeight: 600,
    },
    '&.Mui-error': {
      color: THEME.ACCENT_ERROR,
    }
  },
  '& .MuiOutlinedInput-root': {
    bgcolor: alpha(THEME.BG_SECONDARY, 0.8),
    color: THEME.TEXT_PRIMARY,
    transition: 'all 0.2s',
    '& input': {
      color: THEME.TEXT_PRIMARY,
      fontSize: '0.95rem',
    },
    '& fieldset': {
      borderColor: THEME.BORDER,
      borderWidth: '1.5px',
    },
    '&:hover': {
      bgcolor: THEME.BG_SECONDARY,
      '& fieldset': {
        borderColor: THEME.BORDER_LIGHT,
      }
    },
    '&.Mui-focused': {
      bgcolor: THEME.BG_SECONDARY,
      boxShadow: `0 0 0 3px ${alpha(THEME.ACCENT_PRIMARY, 0.1)}`,
      '& fieldset': {
        borderColor: THEME.ACCENT_PRIMARY,
        borderWidth: '2px',
      }
    }
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    fontSize: '0.75rem',
  }
};

export default function ChangePassword({ onSuccess }: ChangePasswordProps) {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

const handleSubmit = async (e: React.FormEvent) => {
e.preventDefault();
setError(null);
setSuccess(null);

if (newPassword !== confirmPassword) {
setError("Le nuove password non corrispondono");
return;
}

if (newPassword.length < 6) {
setError("La nuova password deve essere di almeno 6 caratteri");
return;
}

try {
setLoading(true);
const payload: ChangePasswordPayload = {
old_password: oldPassword,
new_password: newPassword,
};
await changePasswordRequest(payload);
setSuccess("Password cambiata con successo!");
setTimeout(() => {
  if (onSuccess) onSuccess();
}, 1500);
} catch (err) {
setError("Errore durante il cambio della password");
} finally {
setLoading(false);
}
};

  // Password strength calculator
  const getPasswordStrength = () => {
    if (!newPassword) return 0;
    let strength = 0;
    if (newPassword.length >= 6) strength += 25;
    if (newPassword.length >= 10) strength += 25;
    if (/[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword)) strength += 25;
    if (/[0-9]/.test(newPassword)) strength += 15;
    if (/[^a-zA-Z0-9]/.test(newPassword)) strength += 10;
    return Math.min(strength, 100);
  };

  const passwordStrength = getPasswordStrength();
  const strengthColor = passwordStrength < 40 ? THEME.ACCENT_ERROR : passwordStrength < 70 ? THEME.ACCENT_WARNING : THEME.ACCENT_SUCCESS;

return (
<Card
  component="form" 
  onSubmit={handleSubmit} 
  elevation={0}
  sx={{ 
    display: "flex", 
    flexDirection: "column", 
    gap: 3,
    px: 4,
    py: 4,
    backgroundColor: THEME.BG_CARD,
    backgroundImage: `
      radial-gradient(circle at 20% 10%, ${alpha(THEME.ACCENT_PRIMARY, 0.08)} 0%, transparent 50%),
      radial-gradient(circle at 80% 90%, ${alpha(THEME.ACCENT_SUCCESS, 0.06)} 0%, transparent 50%)
    `,
    borderRadius: 0,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: `linear-gradient(90deg, ${THEME.ACCENT_PRIMARY}, ${THEME.ACCENT_SUCCESS})`,
    }
  }}
>
  {loading && (
    <LinearProgress 
      sx={{ 
        position: 'absolute',
        top: 2,
        left: 0,
        right: 0,
        bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.1),
        '& .MuiLinearProgress-bar': {
          bgcolor: THEME.ACCENT_PRIMARY
        }
      }} 
    />
  )}

  {/* Modern Header with Icon */}
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 1 }}>
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: '14px',
        background: `linear-gradient(135deg, ${THEME.ACCENT_PRIMARY}, ${THEME.ACCENT_SUCCESS})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: `0 6px 20px ${alpha(THEME.ACCENT_PRIMARY, 0.35)}`,
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'scale(1.05)',
        }
      }}
    >
      <VpnKey sx={{ color: '#fff', fontSize: 30 }} />
    </Box>
    <Box flex={1}>
      <Typography variant="h5" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 700, mb: 0.5 }}>
        Cambia Password
      </Typography>
      <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY, fontSize: '0.9rem' }}>
        Aggiorna la tua password per mantenere sicuro il tuo account
      </Typography>
    </Box>
  </Box>

  {/* Alerts */}
  {error && (
    <Fade in>
      <Alert 
        severity="error" 
        sx={{ 
          bgcolor: alpha(THEME.ACCENT_ERROR, 0.1),
          color: THEME.ACCENT_ERROR,
          border: `1px solid ${alpha(THEME.ACCENT_ERROR, 0.3)}`,
          '& .MuiAlert-icon': { color: THEME.ACCENT_ERROR }
        }}
      >
        {error}
      </Alert>
    </Fade>
  )}

  {success && (
    <Fade in>
      <Alert 
        severity="success" 
        icon={<CheckCircle />}
        sx={{ 
          bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.1),
          color: THEME.ACCENT_SUCCESS,
          border: `1px solid ${alpha(THEME.ACCENT_SUCCESS, 0.3)}`,
          '& .MuiAlert-icon': { color: THEME.ACCENT_SUCCESS }
        }}
      >
        {success}
      </Alert>
    </Fade>
  )}

  {/* Current Password */}
  <Fade in timeout={300}>
    <TextField
      type={showOldPassword ? "text" : "password"}
      label="Password attuale"
      value={oldPassword}
      onChange={(e) => setOldPassword(e.target.value)}
      fullWidth
      required
      sx={textFieldSx}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Lock sx={{ color: THEME.TEXT_MUTED }} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton 
              onClick={() => setShowOldPassword(!showOldPassword)}
              sx={{ color: THEME.TEXT_MUTED }}
            >
              {showOldPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        )
      }}
    />
  </Fade>

  {/* New Password with Strength Indicator */}
  <Fade in timeout={400}>
    <Box>
      <TextField
        type={showNewPassword ? "text" : "password"}
        label="Nuova password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        fullWidth
        required
        disabled={loading}
        sx={textFieldSx}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockOpen sx={{ color: THEME.TEXT_MUTED }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton 
                onClick={() => setShowNewPassword(!showNewPassword)}
                disabled={loading}
                sx={{ color: THEME.TEXT_MUTED }}
              >
                {showNewPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />
      {newPassword && (
        <Box sx={{ mt: 1, px: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: THEME.TEXT_MUTED }}>
              Forza password
            </Typography>
            <Typography variant="caption" sx={{ color: strengthColor, fontWeight: 600 }}>
              {passwordStrength < 40 ? 'Debole' : passwordStrength < 70 ? 'Media' : 'Forte'}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={passwordStrength} 
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: alpha(strengthColor, 0.1),
              '& .MuiLinearProgress-bar': {
                bgcolor: strengthColor,
                borderRadius: 3,
                transition: 'all 0.3s',
              }
            }}
          />
        </Box>
      )}
    </Box>
  </Fade>

  {/* Confirm Password */}
  <Fade in timeout={500}>
    <TextField
      type={showConfirmPassword ? "text" : "password"}
      label="Conferma nuova password"
      value={confirmPassword}
      onChange={(e) => setConfirmPassword(e.target.value)}
      fullWidth
      required
      disabled={loading}
      sx={textFieldSx}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Lock sx={{ color: THEME.TEXT_MUTED }} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            {confirmPassword && newPassword === confirmPassword && (
              <CheckCircle sx={{ color: THEME.ACCENT_SUCCESS, mr: 1 }} />
            )}
            <IconButton 
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              disabled={loading}
              sx={{ color: THEME.TEXT_MUTED }}
            >
              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        )
      }}
    />
  </Fade>

  {/* Submit Button */}
  <Button
    type="submit"
    variant="contained"
    fullWidth
    disabled={loading || !oldPassword || !newPassword || !confirmPassword}
    sx={{
      mt: 2,
      py: 1.75,
      borderRadius: 2.5,
      background: `linear-gradient(135deg, ${THEME.ACCENT_PRIMARY}, ${THEME.ACCENT_SUCCESS})`,
      boxShadow: `0 6px 20px ${alpha(THEME.ACCENT_PRIMARY, 0.35)}`,
      fontWeight: 700,
      fontSize: '1.05rem',
      letterSpacing: '0.3px',
      textTransform: 'none',
      '&:hover': {
        background: `linear-gradient(135deg, ${THEME.ACCENT_SUCCESS}, ${THEME.ACCENT_PRIMARY})`,
        boxShadow: `0 8px 24px ${alpha(THEME.ACCENT_PRIMARY, 0.45)}`,
        transform: 'translateY(-2px)',
      },
      '&:active': {
        transform: 'translateY(0)',
        boxShadow: `0 4px 16px ${alpha(THEME.ACCENT_PRIMARY, 0.35)}`,
      },
      '&:disabled': {
        background: alpha(THEME.TEXT_MUTED, 0.15),
        color: alpha(THEME.TEXT_MUTED, 0.5),
      },
      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    }}
  >
    {loading ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={22} sx={{ color: '#fff' }} />
        <span>Aggiornamento...</span>
      </Box>
    ) : (
      'Aggiorna Password'
    )}
  </Button>
</Card>
);
}